import React from 'react';
import { CheckIcon, ChevronRightIcon, ShieldIcon } from '@newcore/icons';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Text } from '@newcore/ui/typography';
import { Onboarding } from '@/features/onboarding/components/primitives';
import { FACTOR_DEFINITIONS } from '@/features/onboarding/constants/onboarding.constants';
import type {
  FactorType,
  AlternativeFactor,
} from '@/features/onboarding/types/onboarding.types';

interface SecondaryFactorsStepProps {
  alternatives: AlternativeFactor[];
  isLoading: boolean;
  error: string | null;
  canComplete: boolean;
  onContinue: () => Promise<boolean>;
  onClearError: () => void;
  onChooseAlternative: (factorType: FactorType) => Promise<boolean>;
}

export const SecondaryFactorsStep: React.FC<SecondaryFactorsStepProps> = ({
  alternatives,
  isLoading,
  error,
  canComplete,
  onContinue,
  onClearError,
  onChooseAlternative,
}) => {
  if (isLoading && alternatives.length === 0) {
    return <Onboarding.Loading />;
  }

  return (
    <div className='flex flex-col items-center space-y-6 py-2'>
      <Onboarding.Icon>
        <ShieldIcon />
      </Onboarding.Icon>

      <div className='text-center space-y-2'>
        <Onboarding.Title>Choose Authentication Method</Onboarding.Title>
        <Onboarding.Description>
          {canComplete
            ? 'Select an additional method or continue to complete setup'
            : 'Select at least one authentication method to continue'}
        </Onboarding.Description>
      </div>

      <Onboarding.Error error={error} onDismiss={onClearError} />

      <div className='w-full max-w-sm space-y-2'>
        {alternatives.map(factor => {
          // Defensive check: ensure factor definition exists
          const factorDef = FACTOR_DEFINITIONS[factor.factorType];

          if (!factorDef) {
            console.error(
              'Missing factor definition for:',
              factor.factorType,
              factor
            );

            return null; // Skip rendering if factor definition is missing
          }

          const Icon = factorDef.icon;

          const handleClick = () => {
            // Don't allow clicking on already enrolled factors
            if (factor.isEnrolled) {
              return;
            }

            onClearError();
            onChooseAlternative(factor.factorType);
          };

          return (
            <button
              key={factor.factorType}
              onClick={handleClick}
              disabled={isLoading || factor.isEnrolled}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left
                ${
                  factor.isEnrolled
                    ? 'bg-utility-green-50/50 border-utility-green-200/60 cursor-not-allowed'
                    : 'hover:bg-secondary hover:border-brand/20 cursor-pointer border-secondary'
                }
                ${isLoading ? 'opacity-50' : ''}`}
            >
              <div
                className={`flex items-center justify-center h-10 w-10 rounded-full shrink-0
                ${factor.isEnrolled ? 'bg-utility-green-100/80' : 'bg-secondary'}`}
              >
                <Icon
                  className={`h-5 w-5 ${factor.isEnrolled ? 'text-utility-green-600' : 'text-tertiary'}`}
                />
              </div>
              <div className='flex-1 min-w-0'>
                <Text as='p' weight={FontWeights.MEDIUM}>
                  {factor.displayName}
                </Text>
                <Text
                  as='p'
                  size={TextSizes.XS}
                  className='text-tertiary truncate'
                >
                  {factor.description}
                </Text>
              </div>
              {factor.isEnrolled ? (
                <div className='flex items-center justify-center h-6 w-6 rounded-full bg-utility-green-500 shrink-0'>
                  <CheckIcon className='h-3.5 w-3.5 text-white stroke-[3]' />
                </div>
              ) : (
                <ChevronRightIcon className='h-5 w-5 text-tertiary shrink-0' />
              )}
            </button>
          );
        })}
      </div>

      <Onboarding.NextButton
        onClick={onContinue}
        disabled={!canComplete || isLoading}
        loading={isLoading}
        className='w-full max-w-sm'
      >
        Complete
      </Onboarding.NextButton>
    </div>
  );
};
